import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '../helpers'
import { ApartmentContract } from '../models/Company/types'
import { updateContractRequest } from './useCompanyContracts'

interface UseVendorContractsOptions {
    vendorId: number
}

const getQueryKey = (vendorId: number) => ['vendorContracts', vendorId]

export const useVendorContracts = ({ vendorId }: UseVendorContractsOptions) => {
    const queryClient = useQueryClient()
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())

    // Get contracts filtered by vendor
    const {
        data: contracts,
        isLoading: isLoadingContracts,
        error: errorContracts,
        refetch: refetchContracts,
    } = useQuery<AxiosResponse<ApartmentContract[]>, Error>({
        queryKey: getQueryKey(vendorId),
        queryFn: () =>
            axiosInstance.get('/company/apartment_contract/', {
                params: { vendor: vendorId },
            }),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: !!vendorId,
    })

    // Create/upload a new contract with vendor
    const createContract = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('vendor', String(vendorId))

            return axiosInstance.post<ApartmentContract>(
                '/company/apartment_contract/',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getQueryKey(vendorId) })
            queryClient.invalidateQueries({ queryKey: ['companyContracts'] })
            toast.success('Contract uploaded successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload contract'
            toast.error(errorMessage)
        },
    })

    // Upload multiple contracts
    const uploadContracts = async (files: File[]) => {
        const results = await Promise.allSettled(
            files.map((file) => createContract.mutateAsync(file)),
        )

        const successCount = results.filter((r) => r.status === 'fulfilled')
            .length
        const failCount = results.filter((r) => r.status === 'rejected').length

        if (failCount > 0 && successCount > 0) {
            toast.warning(
                `Uploaded ${successCount} contract(s), ${failCount} failed`,
            )
        } else if (failCount > 0 && successCount === 0) {
            toast.error('Failed to upload contracts')
        }

        return results
    }

    // Process/analyze a contract with AI
    const processContract = useMutation({
        mutationFn: async (contractId: number) => {
            setProcessingIds((prev) => new Set(prev).add(contractId))
            return axiosInstance.post(
                `/company/apartment_contract/${contractId}/process_contract/`,
            )
        },
        onSuccess: (_, contractId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(contractId)
                return next
            })
            queryClient.invalidateQueries({ queryKey: getQueryKey(vendorId) })
            queryClient.invalidateQueries({ queryKey: ['companyContracts'] })
            toast.success('Contract analyzed successfully')
        },
        onError: (error: any, contractId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(contractId)
                return next
            })
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to analyze contract'
            toast.error(errorMessage)
        },
    })

    // Update a contract
    const updateContract = useMutation({
        mutationFn: async ({
            contractId,
            data,
        }: {
            contractId: number
            data: updateContractRequest
        }) => {
            return axiosInstance.patch<ApartmentContract>(
                `/company/apartment_contract/${contractId}/`,
                data,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getQueryKey(vendorId) })
            queryClient.invalidateQueries({ queryKey: ['companyContracts'] })
            queryClient.invalidateQueries({ queryKey: ['contractAnalytics'] })
            queryClient.invalidateQueries({ queryKey: ['contractAlerts'] })
            toast.success('Contract updated successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update contract'
            toast.error(errorMessage)
        },
    })

    // Delete a contract
    const deleteContract = useMutation({
        mutationFn: async (contractId: number) => {
            return axiosInstance.delete(
                `/company/apartment_contract/${contractId}/`,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getQueryKey(vendorId) })
            queryClient.invalidateQueries({ queryKey: ['companyContracts'] })
            toast.success('Contract deleted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete contract'
            toast.error(errorMessage)
        },
    })

    return {
        contracts: contracts?.data || [],
        isLoadingContracts,
        errorContracts,
        refetchContracts,
        createContract,
        uploadContracts,
        processContract,
        updateContract,
        deleteContract,
        isUploading: createContract.isLoading,
        isProcessing: processContract.isLoading,
        isUpdating: updateContract.isLoading,
        isDeleting: deleteContract.isLoading,
        processingIds,
    }
}

